import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { dummyP2PUserPaymentMethods } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./P2PUserPaymentMethodsPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso || "N/A";
  }
};

const P2PUserPaymentMethodsPage = () => {
  const [methods, setMethods] = useState(dummyP2PUserPaymentMethods);
  const [statusFilter, setStatusFilter] = useState("All");
  const [userSearch, setUserSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ paymentType: "", accountDetails: "", fiatCurrency: "" });
  const [viewingRow, setViewingRow] = useState(null);

  const filtered = useMemo(() => {
    return methods.filter((m) => {
      if (statusFilter !== "All" && (m.status || "").toUpperCase() !== statusFilter.toUpperCase()) return false;
      if (userSearch.trim()) {
        const q = userSearch.toLowerCase();
        if (!(m.userEmail || "").toLowerCase().includes(q) && !(m.userName || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [methods, statusFilter, userSearch]);

  const counts = useMemo(() => ({
    total: methods.length,
    active: methods.filter((m) => (m.status || "").toUpperCase() === "ACTIVE").length,
    inactive: methods.filter((m) => (m.status || "").toUpperCase() === "INACTIVE").length,
  }), [methods]);

  const resetFilters = () => {
    setStatusFilter("All");
    setUserSearch("");
  };

  const handleToggleStatus = (id) => {
    setMethods((prev) =>
      prev.map((m) =>
        m._id === id
          ? { ...m, status: (m.status || "").toUpperCase() === "ACTIVE" ? "INACTIVE" : "ACTIVE" }
          : m
      )
    );
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this payment method?")) {
      setMethods((prev) => prev.filter((m) => m._id !== id));
    }
  };

  const handleEditClick = (row) => {
    setEditingId(row._id);
    setEditForm({
      paymentType: row.paymentType || "",
      accountDetails: row.accountDetails || "",
      fiatCurrency: row.fiatCurrency || "",
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingId) return;
    setMethods((prev) =>
      prev.map((m) =>
        m._id === editingId
          ? { ...m, ...editForm, paymentType: editForm.paymentType.trim(), accountDetails: editForm.accountDetails.trim(), fiatCurrency: (editForm.fiatCurrency || "").trim().toUpperCase() }
          : m
      )
    );
    setEditingId(null);
    setEditForm({ paymentType: "", accountDetails: "", fiatCurrency: "" });
  };

  return (
    <main className="dashboard-main dashboard-subpage p2p-payment-methods-page">
      <section className="dashboard-hero p2p-payment-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>User Payment Methods</span>
          </p>
          <h1 className="dashboard-hero__title">P2P User Payment Methods</h1>
          <p className="dashboard-hero__subtitle">View and manage user payment methods for P2P trading.</p>
        </div>
        <div className="p2p-payment-hero__icon">
          <i className="fas fa-credit-card" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="p2p-payment-stats">
          <div className="p2p-payment-stat-card">
            <i className="fas fa-list p2p-payment-stat-card__icon p2p-payment-stat-card__icon--default" />
            <span className="p2p-payment-stat-card__value">{counts.total}</span>
            <span className="p2p-payment-stat-card__label">Total</span>
          </div>
          <div className="p2p-payment-stat-card">
            <i className="fas fa-check-circle p2p-payment-stat-card__icon p2p-payment-stat-card__icon--active" />
            <span className="p2p-payment-stat-card__value">{counts.active}</span>
            <span className="p2p-payment-stat-card__label">Active</span>
          </div>
          <div className="p2p-payment-stat-card">
            <i className="fas fa-times-circle p2p-payment-stat-card__icon p2p-payment-stat-card__icon--inactive" />
            <span className="p2p-payment-stat-card__value">{counts.inactive}</span>
            <span className="p2p-payment-stat-card__label">Inactive</span>
          </div>
        </div>

        <section className="p2p-payment-filters-section">
          <h2 className="p2p-payment-filters__title"><i className="fas fa-filter" /> Filters</h2>
          <div className="p2p-payment-filters-row">
            <div className="p2p-payment-filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div className="p2p-payment-filter-group">
              <label>User (email / name)</label>
              <input type="text" placeholder="Search..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
            </div>
            <button type="button" className="p2p-payment-btn-reset" onClick={resetFilters}>
              <i className="fas fa-sync-alt" /> Reset Filters
            </button>
          </div>
        </section>

        <section className="p2p-payment-list-section">
          <h2 className="p2p-payment-list__title"><i className="fas fa-th-list" /> Payment Methods List ({filtered.length})</h2>
          <div className="p2p-payment-table-wrap">
            <table className="p2p-payment-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>USER</th>
                  <th>PAYMENT TYPE</th>
                  <th>ACCOUNT DETAILS</th>
                  <th>FIAT CURRENCY</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="p2p-payment-user">
                        <span className="p2p-payment-user__name">{row.userName}</span>
                        <span className="p2p-payment-user__email">{row.userEmail}</span>
                      </div>
                    </td>
                    <td>{row.paymentType}</td>
                    <td>{row.accountDetails}</td>
                    <td>{row.fiatCurrency}</td>
                    <td><span className={`p2p-payment-status-badge p2p-payment-status-badge--${(row.status || "").toLowerCase()}`}>{row.status}</span></td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <div className="p2p-payment-actions">
                        <button type="button" className="p2p-payment-action-btn p2p-payment-action-btn--view" onClick={() => setViewingRow(row)}>View</button>
                        <button type="button" className="p2p-payment-action-btn p2p-payment-action-btn--edit" onClick={() => handleEditClick(row)}>Edit</button>
                        {(row.status || "").toUpperCase() === "ACTIVE" ? (
                          <button type="button" className="p2p-payment-action-btn p2p-payment-action-btn--deactivate" onClick={() => handleToggleStatus(row._id)}>Deactivate</button>
                        ) : (
                          <button type="button" className="p2p-payment-action-btn p2p-payment-action-btn--activate" onClick={() => handleToggleStatus(row._id)}>Activate</button>
                        )}
                        <button type="button" className="p2p-payment-action-btn p2p-payment-action-btn--delete" onClick={() => handleDelete(row._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {viewingRow && (
        <div className="p2p-payment-modal-overlay" onClick={() => setViewingRow(null)}>
          <div className="p2p-payment-modal p2p-payment-modal--view" onClick={(e) => e.stopPropagation()}>
            <h3 className="p2p-payment-modal__title">Payment Method Details</h3>
            <div className="p2p-payment-view-body">
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">User</span><span>{viewingRow.userName} ({viewingRow.userEmail})</span></div>
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">Payment Type</span><span>{viewingRow.paymentType}</span></div>
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">Account Details</span><span>{viewingRow.accountDetails}</span></div>
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">Fiat Currency</span><span>{viewingRow.fiatCurrency}</span></div>
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">Status</span><span className={`p2p-payment-status-badge p2p-payment-status-badge--${(viewingRow.status || "").toLowerCase()}`}>{viewingRow.status}</span></div>
              <div className="p2p-payment-view-row"><span className="p2p-payment-view-label">Created At</span><span>{formatDate(viewingRow.createdAt)}</span></div>
            </div>
            <div className="p2p-payment-modal__actions">
              <button type="button" className="p2p-payment-modal-btn p2p-payment-modal-btn--cancel" onClick={() => setViewingRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingId && (
        <div className="p2p-payment-modal-overlay" onClick={() => setEditingId(null)}>
          <div className="p2p-payment-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="p2p-payment-modal__title">Edit Payment Method</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="p2p-payment-modal__group">
                <label>Payment Type</label>
                <select value={editForm.paymentType} onChange={(e) => setEditForm((f) => ({ ...f, paymentType: e.target.value }))} required>
                  <option value="">Select</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="p2p-payment-modal__group">
                <label>Account Details</label>
                <input type="text" placeholder="e.g. account or UPI id" value={editForm.accountDetails} onChange={(e) => setEditForm((f) => ({ ...f, accountDetails: e.target.value }))} required />
              </div>
              <div className="p2p-payment-modal__group">
                <label>Fiat Currency</label>
                <input type="text" placeholder="e.g. INR, USD" value={editForm.fiatCurrency} onChange={(e) => setEditForm((f) => ({ ...f, fiatCurrency: e.target.value }))} maxLength={5} required />
              </div>
              <div className="p2p-payment-modal__actions">
                <button type="button" className="p2p-payment-modal-btn p2p-payment-modal-btn--cancel" onClick={() => setEditingId(null)}>Cancel</button>
                <button type="submit" className="p2p-payment-modal-btn p2p-payment-modal-btn--submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default P2PUserPaymentMethodsPage;
